import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import Contact from "@/model/interface/Contact";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGetContactDuplicatesQuery, useGetContactsQuery, useMergeContactsMutation } from "@/store/rest_api";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { apiErrorMessage } from "@/util/apiError";
import { getContactDisplayName, getInitials } from "@/util/string";

interface MergeContactDialogProps {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const UNNAMED_CONTACT = "Unnamed contact";

const MergeContactDialog: React.FC<MergeContactDialogProps> = ({ contact, open, onOpenChange }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: duplicates = [] } = useGetContactDuplicatesQuery(contact.id, { skip: !open });
  const { data: searchResults } = useGetContactsQuery(
    { q: debouncedSearch, size: 10 },
    { skip: !open || debouncedSearch.length < MIN_SEARCH_LENGTH },
  );
  const [mergeContacts, { isLoading: isMerging }] = useMergeContactsMutation();

  const candidates = useMemo(() => {
    const seen = new Set<string>([contact.id]);
    const list: Contact[] = [];
    [...duplicates, ...(searchResults?.items ?? [])].forEach((candidate) => {
      if (seen.has(candidate.id)) {
        return;
      }
      seen.add(candidate.id);
      list.push(candidate);
    });
    return list;
  }, [contact.id, duplicates, searchResults]);
  const duplicateIds = useMemo(() => new Set(duplicates.map((duplicate) => duplicate.id)), [duplicates]);
  const selected = candidates.find((candidate) => candidate.id === selectedId) ?? null;
  const currentIsOlder = selected ? (contact.createdAt ?? "") <= (selected.createdAt ?? "") : true;
  const survivor = currentIsOlder ? contact : selected;
  const removed = currentIsOlder ? selected : contact;

  const handleMerge = async () => {
    if (!selected) {
      return;
    }
    try {
      const merged = await mergeContacts({ id: contact.id, sourceId: selected.id }).unwrap();
      toast.success("Contacts merged");
      onOpenChange(false);
      navigate(`/contacts/${merged.id}/detail`, { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to merge contacts"));
    }
  };

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedId(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Merge contacts</DialogTitle>
          <DialogDescription>
            Pick the duplicate of {getContactDisplayName(contact) ?? UNNAMED_CONTACT}. The older contact is kept, its missing details are filled in from the newer one, and every call, text, deal and task moves over.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, phone or email"
            className="pl-9"
          />
        </div>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          {candidates.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {debouncedSearch.length >= MIN_SEARCH_LENGTH ? "No contacts match." : "No duplicates found. Search for the contact to merge."}
            </div>
          )}
          {candidates.map((candidate) => {
            const isSelected = candidate.id === selectedId;
            return (
              <button
                key={candidate.id}
                type="button"
                onClick={() => setSelectedId(candidate.id)}
                className={`flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-accent ${isSelected ? "bg-primary/5" : ""}`}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-muted text-xs">{getInitials(candidate.firstName, candidate.lastName)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{getContactDisplayName(candidate) ?? UNNAMED_CONTACT}</span>
                    {duplicateIds.has(candidate.id) && <span className="status-pill-warning">Possible duplicate</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {[
                      ...(candidate.phones ?? []).map((phone) => phone.phoneNumber || phone.phoneNumberE164),
                      ...(candidate.emails ?? []).map((contactEmail) => contactEmail.email),
                    ].filter(Boolean).join(" · ") || "No phone or email"}
                    {candidate.createdAt && ` · Created ${format(new Date(candidate.createdAt), "MMM d, yyyy")}`}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {selected && survivor && removed && (
          <p className="text-sm text-muted-foreground">
            Keeps <span className="font-medium text-foreground">{getContactDisplayName(survivor) ?? UNNAMED_CONTACT}</span> and removes{" "}
            <span className="font-medium text-foreground">{getContactDisplayName(removed) ?? UNNAMED_CONTACT}</span> after moving everything over.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleMerge()} disabled={!selected || isMerging}>
            {isMerging ? "Merging..." : "Merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeContactDialog;
